import React from 'react';
import {
    Typography,
    TextField,
    Button,
    Box,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Paper
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Add as AddIcon,
    ExpandMore as ExpandMoreIcon,
    Download as DownloadIcon,
    Print as PrintIcon
} from '@mui/icons-material';

const FormSection = ({
    resumeData,
    handleChange,
    handleArrayChange,
    handleNestedArrayChange,
    handleAddItem,
    handleRemoveItem,
    handleAddAchievement,
    handleRemoveAchievement,
    handleAddHighlight,
    handleRemoveHighlight,
    handlePrint,
    handleDownloadPDF
}) => {
    return (
        <Grid
            item
            xs={12}
            md={6}
            sx={{
                '@media print': { display: 'none' },
                height: '100%'
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                <Typography variant="h4" gutterBottom>
                    Resume Builder
                </Typography>

                <Box
                    sx={{
                        flexGrow: 1,
                        overflow: 'auto',
                        pr: 1 // Add padding for scrollbar
                    }}
                >
                    {/* Personal Information */}
                    <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Personal Information</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Full Name"
                                        value={resumeData.personalInfo.name}
                                        onChange={(e) => handleChange('personalInfo', 'name', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Professional Title"
                                        value={resumeData.personalInfo.title}
                                        onChange={(e) => handleChange('personalInfo', 'title', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        value={resumeData.personalInfo.email}
                                        onChange={(e) => handleChange('personalInfo', 'email', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Phone"
                                        value={resumeData.personalInfo.phone}
                                        onChange={(e) => handleChange('personalInfo', 'phone', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Location"
                                        value={resumeData.personalInfo.location}
                                        onChange={(e) => handleChange('personalInfo', 'location', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="LinkedIn"
                                        value={resumeData.personalInfo.linkedIn}
                                        onChange={(e) => handleChange('personalInfo', 'linkedIn', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Website"
                                        value={resumeData.personalInfo.website}
                                        onChange={(e) => handleChange('personalInfo', 'website', e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Summary */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Professional Summary</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Summary"
                                value={resumeData.summary}
                                onChange={(e) => handleChange('summary', '', e.target.value)}
                            />
                        </AccordionDetails>
                    </Accordion>

                    {/* Work Experience */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Work Experience</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {resumeData.experience.map((exp, index) => (
                                <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="subtitle1">Position {index + 1}</Typography>
                                        {resumeData.experience.length > 1 && (
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleRemoveItem('experience', index)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Company"
                                                value={exp.company}
                                                onChange={(e) => handleArrayChange('experience', index, 'company', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Position"
                                                value={exp.position}
                                                onChange={(e) => handleArrayChange('experience', index, 'position', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Start Date"
                                                value={exp.startDate}
                                                onChange={(e) => handleArrayChange('experience', index, 'startDate', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="End Date"
                                                value={exp.endDate}
                                                onChange={(e) => handleArrayChange('experience', index, 'endDate', e.target.value)}
                                                placeholder="Present (if current)"
                                            />
                                        </Grid>
                                        {/* Achievements */}
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" gutterBottom>Key Achievements</Typography>
                                            {exp.achievements.map((achievement, achievementIndex) => (
                                                <Box key={achievementIndex} display="flex" alignItems="center" mb={1}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label={`Achievement ${achievementIndex + 1}`}
                                                        value={achievement}
                                                        onChange={(e) => handleNestedArrayChange(
                                                            'experience',
                                                            index,
                                                            'achievements',
                                                            achievementIndex,
                                                            e.target.value
                                                        )}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleRemoveAchievement(index, achievementIndex)}
                                                        disabled={exp.achievements.length <= 1}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                            <Button
                                                startIcon={<AddIcon />}
                                                onClick={() => handleAddAchievement(index)}
                                                size="small"
                                                sx={{ mt: 1 }}
                                            >
                                                Add Achievement
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ))}
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => handleAddItem('experience')}
                                fullWidth
                                sx={{ mt: 2 }}
                            >
                                Add Experience
                            </Button>
                        </AccordionDetails>
                    </Accordion>

                    {/* Education */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Education</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {resumeData.education.map((edu, index) => (
                                <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="subtitle1">Education {index + 1}</Typography>
                                        {resumeData.education.length > 1 && (
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleRemoveItem('education', index)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Institution"
                                                value={edu.institution}
                                                onChange={(e) => handleArrayChange('education', index, 'institution', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Degree"
                                                value={edu.degree}
                                                onChange={(e) => handleArrayChange('education', index, 'degree', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Field of Study"
                                                value={edu.field}
                                                onChange={(e) => handleArrayChange('education', index, 'field', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Graduation Date"
                                                value={edu.graduationDate}
                                                onChange={(e) => handleArrayChange('education', index, 'graduationDate', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="GPA"
                                                value={edu.gpa}
                                                onChange={(e) => handleArrayChange('education', index, 'gpa', e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            ))}
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => handleAddItem('education')}
                                fullWidth
                                sx={{ mt: 2 }}
                            >
                                Add Education
                            </Button>
                        </AccordionDetails>
                    </Accordion>

                    {/* Skills */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Skills</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {resumeData.skills.map((skill, index) => (
                                <Box key={index} display="flex" alignItems="center" mb={1}>
                                    <TextField
                                        fullWidth
                                        label={`Skill ${index + 1}`}
                                        value={skill}
                                        onChange={(e) => handleArrayChange('skills', index, '', e.target.value)}
                                    />
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveItem('skills', index)}
                                        disabled={resumeData.skills.length <= 1}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            ))}
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => handleAddItem('skills')}
                                fullWidth
                                sx={{ mt: 2 }}
                            >
                                Add Skill
                            </Button>
                        </AccordionDetails>
                    </Accordion>

                    {/* Projects */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Projects</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {resumeData.projects.map((project, index) => (
                                <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="subtitle1">Project {index + 1}</Typography>
                                        {resumeData.projects.length > 1 && (
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleRemoveItem('projects', index)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Project Title"
                                                value={project.title}
                                                onChange={(e) => handleArrayChange('projects', index, 'title', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Role"
                                                value={project.role}
                                                onChange={(e) => handleArrayChange('projects', index, 'role', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Start Date"
                                                value={project.startDate}
                                                onChange={(e) => handleArrayChange('projects', index, 'startDate', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="End Date"
                                                value={project.endDate}
                                                onChange={(e) => handleArrayChange('projects', index, 'endDate', e.target.value)}
                                                placeholder="Present (if current)"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Technologies Used"
                                                value={project.technologies}
                                                onChange={(e) => handleArrayChange('projects', index, 'technologies', e.target.value)}
                                                placeholder="React, Node.js, MongoDB, etc."
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Project URL"
                                                value={project.url}
                                                onChange={(e) => handleArrayChange('projects', index, 'url', e.target.value)}
                                                placeholder="https://example.com"
                                            />
                                        </Grid>
                                        {/* Highlights */}
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" gutterBottom>Key Highlights</Typography>
                                            {project.highlights.map((highlight, highlightIndex) => (
                                                <Box key={highlightIndex} display="flex" alignItems="center" mb={1}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label={`Highlight ${highlightIndex + 1}`}
                                                        value={highlight}
                                                        onChange={(e) => handleNestedArrayChange(
                                                            'projects',
                                                            index,
                                                            'highlights',
                                                            highlightIndex,
                                                            e.target.value
                                                        )}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleRemoveHighlight(index, highlightIndex)}
                                                        disabled={project.highlights.length <= 1}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                            <Button
                                                startIcon={<AddIcon />}
                                                onClick={() => handleAddHighlight(index)}
                                                size="small"
                                                sx={{ mt: 1 }}
                                            >
                                                Add Highlight
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ))}
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => handleAddItem('projects')}
                                fullWidth
                                sx={{ mt: 2 }}
                            >
                                Add Project
                            </Button>
                        </AccordionDetails>
                    </Accordion>
                </Box>

                {/* Button Section - Fixed at bottom */}
                <Box sx={{ mt: 3, pt: 2, display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #eee' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                    >
                        Print Resume
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadPDF}
                    >
                        Download PDF
                    </Button>
                </Box>
            </Paper>
        </Grid>
    );
};

export default FormSection;